import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 100px 20px 40px; /* Increased top padding to 100px */
`;

const BackButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-light);
  font-size: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 20px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: rgba(59, 130, 246, 0.1);
    transform: translateX(-5px);
  }
  
  svg {
    font-size: 14px;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
  color: #fff;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 10px;
`;

const Section = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 600;
  margin-bottom: 15px;
  color: var(--primary-light);
`;

const Paragraph = styled.p`
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 15px;
  color: rgba(255, 255, 255, 0.8);
`;

const List = styled.ul`
  margin-left: 20px;
  margin-bottom: 15px;
`;

const ListItem = styled.li`
  font-size: 15px;
  line-height: 1.6;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.8);
`;

const ContactInfo = styled.div`
  background-color: rgba(59, 130, 246, 0.1);
  border-radius: 8px;
  padding: 15px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  margin-top: 20px;
`;

const EmailLink = styled.a`
  color: var(--primary-light);
  text-decoration: underline;
  
  &:hover {
    color: var(--primary-color);
  }
`;

const DMCA = () => {
  const navigate = useNavigate();
  
  const handleBack = () => {
    navigate(-1);
  };
  
  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <FontAwesomeIcon icon={faArrowLeft} />
        Back
      </BackButton>
      
      <Title>DMCA Policy</Title>
      
      <Section>
        <Paragraph>
          Odelu.fun respects the intellectual property rights of others and expects its users to do the same. 
          In accordance with the Digital Millennium Copyright Act of 1998 ("DMCA"), we will respond expeditiously 
          to claims of copyright infringement that are reported to our designated copyright agent.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Content Disclaimer</SectionTitle>
        <Paragraph>
          <strong>Important:</strong> Odelu.fun does not host any content on our servers. We function solely as a 
          frontend interface to content that is hosted elsewhere on the internet. We do not upload, store, or distribute 
          any videos, movies, TV shows, or any other copyrighted content.
        </Paragraph>
        <Paragraph>
          All content accessible through our service is hosted by third-party providers over which we have no control. 
          We simply provide an interface that allows users to access content that is already publicly available on the internet.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>DMCA Takedown Requests</SectionTitle>
        <Paragraph>
          If you believe that any content accessible through our service infringes your copyright, you may request removal 
          of that content by submitting a written notification to our designated copyright agent. To be effective, your 
          notification must include the following:
        </Paragraph>
        <List>
          <ListItem>A physical or electronic signature of a person authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.</ListItem>
          <ListItem>Identification of the copyrighted work claimed to have been infringed, or, if multiple copyrighted works are covered by a single notification, a representative list of such works.</ListItem>
          <ListItem>Identification of the material that is claimed to be infringing or to be the subject of infringing activity and that is to be removed or access to which is to be disabled, and information reasonably sufficient to permit us to locate the material.</ListItem>
          <ListItem>Information reasonably sufficient to permit us to contact the complaining party, such as an address, telephone number, and, if available, an email address.</ListItem>
          <ListItem>A statement that the complaining party has a good faith belief that use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law.</ListItem>
          <ListItem>A statement that the information in the notification is accurate, and under penalty of perjury, that the complaining party is authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.</ListItem>
        </List>
      </Section>
      
      <Section>
        <SectionTitle>Our Response</SectionTitle>
        <Paragraph>
          Upon receipt of a valid DMCA notice, we will make reasonable efforts to promptly investigate and remove links to content 
          that is claimed to be infringing. Please note that since we do not host the content ourselves, our action is limited to 
          removing links to the allegedly infringing content.
        </Paragraph>
        <Paragraph>
          We reserve the right to remove content without prior notice. We also reserve the right to terminate, in appropriate 
          circumstances, users who are repeat infringers.
        </Paragraph>
      </Section>
      
      <Section>
        <SectionTitle>Contact Information</SectionTitle>
        <Paragraph>
          If you have any questions about our DMCA Policy or if you would like to submit a DMCA notice, please contact our designated copyright agent:
        </Paragraph>
        <ContactInfo>
          <Paragraph>
            By email: <EmailLink href="mailto:odelu0@proton.me">odelu0@proton.me</EmailLink>
          </Paragraph>
          <Paragraph>
            Website: <EmailLink href="https://odelu.fun" target="_blank" rel="noopener noreferrer">odelu.fun</EmailLink>
          </Paragraph>
        </ContactInfo>
      </Section>
    </PageContainer>
  );
};

export default DMCA; 